import { t } from '../../shared/config/translations'
import { getSessionSignal } from '../api/requestSession'
import { dialogTemplate } from './dialogTemplates'
import { renderSpinner } from './Loader'
import { showSnackbar } from './Snackbar'

interface DialogOptions {
	id?: string
	cancelText?: string
	confirmText?: string
	content: string
	title?: string
	onSuccessMessage?: string
	onErrorMessage?: string
	snackbarType?: 'default' | 'error' | null
}

class DialogController {
	private readonly rootEl: HTMLDialogElement
	private readonly formEl: HTMLFormElement | null
	private readonly confirmButton: HTMLButtonElement | null
	private readonly cancelButton: HTMLButtonElement | null
	private readonly spinner: HTMLDivElement | null
	private readonly onConfirm: (formData?: FormData) => Promise<void> | void
	private readonly onSuccessMessage?: string
	private readonly onErrorMessage?: string
	private readonly snackbarType: 'default' | 'error' | null
	private readonly sessionSignal = getSessionSignal()

	constructor(
		rootEl: HTMLDialogElement,
		onConfirm: (formData?: FormData) => Promise<void> | void,
		options: Pick<
			DialogOptions,
			'onSuccessMessage' | 'onErrorMessage' | 'snackbarType'
		>,
	) {
		this.rootEl = rootEl
		this.formEl = rootEl.querySelector('form')
		this.confirmButton = rootEl.querySelector<HTMLButtonElement>(
			'#dialog-confirm-btn',
		)
		this.cancelButton =
			rootEl.querySelector<HTMLButtonElement>('#dialog-cancel-btn')
		this.spinner = this.confirmButton
			? renderSpinner(this.confirmButton)
			: null
		this.onConfirm = onConfirm
		this.onSuccessMessage = options.onSuccessMessage
		this.onErrorMessage = options.onErrorMessage
		this.snackbarType =
			options.snackbarType === undefined
				? 'default'
				: options.snackbarType

		this.handleConfirm = this.handleConfirm.bind(this)
		this.handleCancel = this.handleCancel.bind(this)

		if (
			!this.formEl ||
			!this.confirmButton ||
			!this.cancelButton ||
			!this.spinner
		)
			return

		this.init()
	}
	private init(): void {
		this.addEventListeners()
	}
	private addEventListeners(): void {
		this.confirmButton?.addEventListener('click', this.handleConfirm)
		this.cancelButton?.addEventListener('click', this.handleCancel)
	}

	private async handleConfirm(evt: MouseEvent): Promise<void> {
		evt.preventDefault()
		if (!this.formEl || !this.spinner) return

		this.spinner.classList.add('visible')
		const formData = new FormData(this.formEl)

		try {
			await this.onConfirm(formData)
			if (this.sessionSignal.aborted || !this.rootEl.isConnected) return
			this.closeAndRemove()
			if (this.snackbarType !== null) {
				const message =
					this.onSuccessMessage ?? t('dialog.successGeneric')
				showSnackbar(message, this.snackbarType)
			}
		} catch (_error) {
			if (this.sessionSignal.aborted || !this.rootEl.isConnected) return
			const message = this.onErrorMessage ?? t('dialog.errorGeneric')
			showSnackbar(message, 'error')
		} finally {
			this.spinner.classList.remove('visible')
		}
	}

	private handleCancel(): void {
		this.closeAndRemove()
	}
	private closeAndRemove(): void {
		const snackbarContainer = this.rootEl.querySelector(
			'#snackbar-container',
		)
		if (snackbarContainer) document.body.append(snackbarContainer)
		this.rootEl.close()
		this.rootEl.remove()
	}
}

export default function initDialog({
	onConfirm,
	...props
}: DialogOptions & {
	onConfirm: (formData?: FormData) => Promise<void> | void
}): void {
	// Ensures only one dialog is open at a time.
	document.querySelector('dialog')?.remove()
	const dialogId = props.id ?? 'tt-dialog'
	const cancelText = props.cancelText ?? 'Cancel'
	const confirmText = props.confirmText ?? 'OK'

	document.body.insertAdjacentHTML(
		'beforeend',
		dialogTemplate({
			id: dialogId,
			cancelText,
			confirmText,
			content: props.content,
			title: props.title,
		}),
	)

	const dialog = document.getElementById(dialogId)
	if (!(dialog instanceof HTMLDialogElement)) return
	dialog.showModal()

	new DialogController(dialog, onConfirm, {
		onSuccessMessage: props.onSuccessMessage,
		onErrorMessage: props.onErrorMessage,
		snackbarType: props.snackbarType,
	})
}

/** Removes modal dialogs when the authenticated session ends. */
export function closeAllDialogs(): void {
	document.querySelectorAll('dialog').forEach((dialog) => {
		if (dialog.open) dialog.close()
		dialog.remove()
	})
}
